import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Award, Leaf, Heart, Brain, Users, X } from 'lucide-react';
import BreathingRoomAttribution from '../components/BreathingRoomAttribution';

const BOOKING_URL = "https://breathingroombda.com/appointments-2/";

// Short, editable write-ups in Jill's voice — tap a modality to learn more.
const MODALITIES = {
  'human-garage': {
    label: 'Human Garage Fascial Maneuvers',
    body: "Fascia is the living web of connective tissue that wraps every muscle, bone, and organ — and it holds our tension, our history, and our patterns. Human Garage Maneuvers are gentle, specific movements (hands-on and self-guided) that help release what your body has been gripping onto, sometimes for years. When the fascia lets go, you feel lighter, looser, and more like yourself. It's the most transformative work I've ever encountered — and the heart of what I do.",
  },
  'somatic': {
    label: 'Somatic practices',
    body: "\"Soma\" means the body as felt from the inside. Somatic work is about listening to your body's signals — breath, sensation, tension, ease — instead of overriding them. We slow down just enough to feel what's actually happening, so your nervous system can finally exhale and begin to re-pattern. It's less about pushing through and more about coming home to yourself.",
  },
  'functional-movement': {
    label: 'functional movement',
    body: "Movement that trains your body for real life — not just for the gym mirror. Rather than isolating one muscle at a time, we build strength, mobility, and coordination in the patterns you actually use every day: bending, reaching, lifting, twisting, getting up off the floor. The goal is simple — to move well, feel strong, and stay pain-free in your real, everyday life.",
  },
  'self-love': {
    label: 'self love/compassion',
    body: "This is the quiet foundation everything else rests on. Lasting healing rarely comes from self-criticism or force. Learning to meet yourself with genuine kindness — especially on the hard days — is what makes real change possible. It isn't fluffy or soft; it's some of the bravest, most grounding work you'll ever do.",
  },
  'metabolic-typing': {
    label: 'metabolic typing',
    body: "There is no single perfect diet — and anyone who tells you otherwise is selling something. Your body has its own unique way of turning food into energy, and what nourishes one person can leave another drained or inflamed. Metabolic typing helps us find the foods, ratios, and rhythms that genuinely work for you, so eating becomes fuel and support rather than guesswork.",
  },
  'gut-healing': {
    label: 'gut healing protocols',
    body: "So much of how we feel — energy, mood, immunity, mental clarity — begins in the gut. Using proven approaches like the SCD (Specific Carbohydrate Diet) and Breaking the Vicious Cycle, we calm inflammation, restore balance, and rebuild the foundation of whole-body health from the inside out. Heal the gut, and so often the rest of the body starts to follow.",
  },
};

const AboutPage = () => {
  const [activeModality, setActiveModality] = useState(null);
  const ModalityTerm = ({ id }) => (
    <button
      onClick={() => setActiveModality(id)}
      className="text-terracotta font-medium underline decoration-dotted decoration-terracotta/50 underline-offset-4 hover:decoration-terracotta hover:text-terracotta-dark transition-colors"
      data-testid={`modality-term-${id}`}
    >
      {MODALITIES[id].label}
    </button>
  );
  const fadeUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] }
  };

  const credentials = [
    'Human Garage Fascial Maneuvers Certified',
    'Certified Personal Trainer',
    'Holistic Health Coach',
    'Metabolic Typing Advisor',
    'IMESP Practitioner',
    'SCD / Breaking the Vicious Cycle Protocol',
  ];

  const values = [
    {
      icon: Leaf,
      title: 'Back to Basics',
      description: 'No gimmicks, no shortcuts. Real healing happens when we return to the fundamentals of movement, nutrition, and rest.',
    },
    {
      icon: Heart,
      title: 'Body Wisdom',
      description: 'Your body isn\'t broken—it\'s communicating. I help you learn to listen and respond with care.',
    },
    {
      icon: Brain,
      title: 'Whole Self Integration',
      description: 'We are not just bodies. We are physical beings having a spiritual experience, and healing must address all aspects.',
    },
    {
      icon: Users,
      title: 'Slow & Sustainable',
      description: 'In a culture of quick fixes, I offer something radical: taking the time to actually heal.',
    },
  ];

  return (
    <div className="bg-cream pt-24" data-testid="about-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <p className="text-terracotta font-body text-sm tracking-widest uppercase mb-4">
                About Jill
              </p>
              <h1 className="font-heading text-5xl md:text-6xl text-forest leading-[1.1] mb-6">
                My Story of<br />
                <span className="italic text-terracotta">Coming Home</span>
              </h1>
              <p className="text-stone-600 text-lg font-body leading-relaxed">
                I didn't find this path through perfect health or a linear career trajectory. 
                I found it through breakdown, questioning, and the slow work of putting 
                myself back together.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.3 }}
            >
              <img
                src="https://customer-assets.emergentagent.com/job_restore-website/artifacts/5262co5f_AHHW%20me%201%20%282%29.jpg"
                alt="Jill - Grounded Wellness practitioner"
                className="rounded-2xl w-full h-[500px] object-cover object-top shadow-soft"
                data-testid="about-hero-image"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-16 md:py-24 bg-stone-100">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="prose prose-lg max-w-none"
          >
            <h2 className="font-heading text-3xl md:text-4xl text-forest mb-8">
              The Meltdown That Changed Everything
            </h2>
            
            <div className="space-y-6 text-stone-600 font-body text-lg leading-relaxed">
              <p>
                Years ago, I experienced what I can only describe as a complete life meltdown—a 
                major life event that shattered my identity and everything I thought I knew about 
                myself. At the time, it felt like destruction. Looking back, it was the beginning 
                of real transformation.
              </p>
              
              <p>
                When a physical setback meets an emotional and spiritual crisis, there's no quick 
                fix. No biohack. No optimization strategy. There's only the slow, sometimes 
                painful, profoundly healing work of coming back to yourself.
              </p>
              
              <p>
                Through that journey, I discovered what I now call whole-method healing—addressing 
                not just the body, but the Intuitive, Mental, Emotional, Spiritual, and Physical 
                aspects of who we are. This isn't about transcending the body or using spirituality 
                to bypass physical reality. It's about integration. We are physical beings having 
                a spiritual existence.
              </p>
              
              <p>
                My training in <ModalityTerm id="human-garage" />—the most transformative modality
                I've ever encountered—along with <ModalityTerm id="somatic" />, <ModalityTerm id="functional-movement" />, <ModalityTerm id="self-love" />, <ModalityTerm id="metabolic-typing" />, <ModalityTerm id="gut-healing" />, and years of personal
                exploration, all feed into the grounded, practical, no-nonsense approach I bring
                to my work today.
              </p>
              <p className="text-sm text-stone-500 italic !mt-2">Tap any highlighted practice above to learn a little more.</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Modality info modal */}
      <AnimatePresence>
        {activeModality && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-forest/40 backdrop-blur-sm"
            onClick={() => setActiveModality(null)}
            data-testid="modality-modal-overlay"
          >
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24, scale: 0.97 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              className="relative bg-cream rounded-3xl shadow-2xl max-w-lg w-full p-8 md:p-10"
              onClick={(e) => e.stopPropagation()}
              data-testid="modality-modal"
            >
              <button
                onClick={() => setActiveModality(null)}
                className="absolute top-5 right-5 text-stone-400 hover:text-forest transition-colors"
                data-testid="modality-modal-close"
                aria-label="Close"
              >
                <X size={22} />
              </button>
              <div className="w-12 h-1 rounded-full bg-terracotta/60 mb-5" />
              <h3 className="font-heading text-2xl md:text-3xl text-forest mb-4 pr-8">{MODALITIES[activeModality].label}</h3>
              <p className="font-body text-stone-600 leading-relaxed">{MODALITIES[activeModality].body}</p>
              <Link
                to="/contact"
                onClick={() => setActiveModality(null)}
                className="inline-flex items-center gap-2 mt-6 text-terracotta font-medium hover:gap-3 transition-all"
                data-testid="modality-modal-cta"
              >
                Curious if this is for you? Let's talk <ArrowRight size={16} />
              </Link>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Philosophy Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <p className="text-terracotta font-body text-sm tracking-widest uppercase mb-4">
              My Philosophy
            </p>
            <h2 className="font-heading text-4xl md:text-5xl text-forest mb-6">
              Why "Grounded" Matters
            </h2>
            <p className="text-stone-600 text-lg font-body leading-relaxed max-w-3xl mx-auto">
              The holistic wellness space can feel overwhelming—full of promises, 
              trends, and approaches that sometimes feel more "airy fairy" than practical. 
              I chose "Grounded" intentionally.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                data-testid={`value-card-${index + 1}`}
                className="bg-stone-100 p-8 rounded-2xl flex gap-6"
              >
                <div className="w-14 h-14 bg-terracotta/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <value.icon size={24} className="text-terracotta" />
                </div>
                <div>
                  <h3 className="font-heading text-xl text-forest mb-3">{value.title}</h3>
                  <p className="text-stone-600 font-body leading-relaxed">{value.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Credentials Section */}
      <section className="py-16 md:py-24 bg-forest text-cream">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <p className="text-terracotta font-body text-sm tracking-widest uppercase mb-4">
                Training & Credentials
              </p>
              <h2 className="font-heading text-4xl md:text-5xl mb-6">
                Rooted in Learning
              </h2>
              <p className="text-cream/80 text-lg font-body leading-relaxed mb-8">
                My approach is informed by years of training, study, and personal practice. 
                I never stop learning because the body—and the self—never stop teaching.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="space-y-4"
            >
              {credentials.map((credential, index) => (
                <div
                  key={index}
                  data-testid={`credential-${index + 1}`}
                  className="flex items-center gap-4 bg-cream/5 p-4 rounded-xl border border-cream/10"
                >
                  <Award size={20} className="text-terracotta flex-shrink-0" />
                  <span className="font-body">{credential}</span>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="font-heading text-4xl md:text-5xl text-forest mb-6">
              Let's Work Together
            </h2>
            <p className="text-stone-600 text-lg font-body leading-relaxed mb-10 max-w-2xl mx-auto">
              If you're feeling disconnected from your body, overwhelmed by health information, 
              or ready to do deeper work on yourself—I'd love to connect.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="about-book-btn"
                className="bg-forest text-cream px-8 py-4 rounded-full font-body font-medium hover:bg-forest-dark transition-all duration-300 inline-flex items-center gap-2"
              >
                Book a Session
                <ArrowRight size={18} />
              </a>
              <Link
                to="/services"
                data-testid="about-services-btn"
                className="border-2 border-forest text-forest px-8 py-4 rounded-full font-body font-medium hover:bg-forest hover:text-cream transition-all duration-300"
              >
                View Services
              </Link>
            </div>
            <div className="mt-6 flex justify-center">
              <BreathingRoomAttribution align="center" />
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
